import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../stores/useAppStore';
import { useRestStore } from '../../stores/useRestStore';
import { useUserStore, getAffinityProgress } from '../../stores/useUserStore';
import { useAudioStore } from '../../stores/useAudioStore';
import { useDressStore } from '../../stores/useDressStore';
import { useSettingsStore } from '../../stores/useSettingsStore';
import { useTodoStore, TodoItem } from '../../stores/useTodoStore';
import { dressList, dressNames } from '../../data/dressList';
import { useTranslation } from '../../i18n/LanguageContext';
import type { BgmTrack } from '../../data/bgmList';
import { getTodayDate } from '../../utils/dateUtils';
import './MascotPanel.css';

type Tab = 'timer' | 'stats' | 'dress' | 'bgm' | 'todo';

// ToDo完了時の褒めセリフ
const TODO_PRAISES = [
  'タスク完了！えらい！',
  'ひとつ片付いたね！すごい！',
  'できたね！ちゃんと見てたよ！',
];

interface MascotPanelProps {
  bgmList: BgmTrack[];
  isBgmPlaying: boolean;
  currentBgmIndex: number | null;
  onPlayBgm: (index: number) => void;
  onStopBgm: () => void;
  onResetTimer: () => void;
  onShowMessage: (message: string, duration?: number) => void;
  onDebugRestChoice?: () => void;
}

const WORK_PRESETS = [5, 15, 25, 45];
const BREAK_PRESETS = [3, 5, 10];

// 円形タイマーの寸法
const RING_RADIUS = 82;
const RING_STROKE = 7;
const RING_SIZE = (RING_RADIUS + RING_STROKE) * 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const formatTimer = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const getDateKey = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const MascotPanel: React.FC<MascotPanelProps> = ({
  bgmList,
  isBgmPlaying,
  currentBgmIndex,
  onPlayBgm,
  onStopBgm,
  onResetTimer,
  onShowMessage,
  onDebugRestChoice,
}) => {
  const [tab, setTab] = useState<Tab>('timer');
  // 購入はワンタップ誤爆を防ぐ二段階式（1回目で確認表示、2回目で確定）
  const [pendingBuyId, setPendingBuyId] = useState<string | null>(null);

  const {
    pomodoroSettings,
    updatePomodoroSettings,
    resetTimer,
    startTimer,
    stopTimer,
    currentSession,
    pomodoroLog,
  } = useAppStore();

  // 円形タイマー用の進捗
  const timerPhase = currentSession.phase;
  const timerDuration =
    (timerPhase === 'break'
      ? pomodoroSettings.breakDuration
      : pomodoroSettings.workDuration) * 60;
  const timerProgress = timerPhase
    ? Math.min(1, (timerDuration - currentSession.timerRemaining) / timerDuration)
    : 0;
  const ringColor =
    timerPhase === 'break'
      ? 'rgba(74, 222, 128, 0.75)'
      : 'rgba(96, 165, 250, 0.85)';
  const { restSettings, updateRestSettings } = useRestStore();
  const { donuts, spendDonut, affinityLevel, affinityExp } = useUserStore();
  const { mascotRandomTalk, setMascotRandomTalk } = useSettingsStore();
  const { todos, addTodo, toggleTodo, removeTodo } = useTodoStore();
  const [todoInput, setTodoInput] = useState('');
  const { unlockBgm } = useAudioStore();
  const { selectedDress, dressUnlocks, selectDress, unlockDress } =
    useDressStore();
  const { language } = useTranslation();

  const switchTab = (next: Tab) => {
    setTab(next);
    setPendingBuyId(null);
  };

  const handlePreset = (type: 'work' | 'break', minutes: number) => {
    if (type === 'work') {
      updatePomodoroSettings({ workDuration: minutes });
    } else {
      updatePomodoroSettings({ breakDuration: minutes });
    }
    // 進行中（一時停止中含む）のセッションは破棄しない
    if (!currentSession.isRunning && !currentSession.phase) {
      resetTimer();
    }
  };

  const stats = useMemo(() => {
    const now = new Date();
    const total = Object.values(pomodoroLog).reduce((sum, m) => sum + m, 0);
    const today = pomodoroLog[getTodayDate()] || 0;

    const days: { label: string; minutes: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      days.push({
        label: String(d.getDate()),
        minutes: pomodoroLog[getDateKey(d)] || 0,
      });
    }
    const week = days.reduce((sum, d) => sum + d.minutes, 0);

    // 今日まだ作業していなくてもストリークは途切れない（昨日から遡る）
    let streak = 0;
    const check = new Date(now);
    if (!pomodoroLog[getDateKey(check)]) {
      check.setDate(check.getDate() - 1);
    }
    while ((pomodoroLog[getDateKey(check)] || 0) > 0) {
      streak++;
      check.setDate(check.getDate() - 1);
    }

    const maxDay = Math.max(...days.map((d) => d.minutes), 1);
    return { total, today, week, days, streak, maxDay };
  }, [pomodoroLog]);

  const handleDressClick = (dressId: string, price: number) => {
    const dress = dressList.find((d) => d.id === dressId);
    if (!dress) return;
    const unlocked = dressUnlocks[dressId] || dress.unlocked;

    if (unlocked) {
      selectDress(dressId);
      return;
    }
    if (pendingBuyId !== `dress:${dressId}`) {
      if (donuts < price) {
        onShowMessage('ドーナツが足りないよ...');
        return;
      }
      setPendingBuyId(`dress:${dressId}`);
      return;
    }
    if (spendDonut(price)) {
      unlockDress(dressId);
      selectDress(dressId);
      onShowMessage('新しい服、ありがとう！', 4000);
    }
    setPendingBuyId(null);
  };

  const handleBgmClick = (track: BgmTrack, index: number) => {
    if (track.unlocked) {
      if (isBgmPlaying && currentBgmIndex === index) {
        onStopBgm();
      } else {
        onPlayBgm(index);
      }
      return;
    }
    if (pendingBuyId !== `bgm:${track.id}`) {
      if (donuts < track.price) {
        onShowMessage('ドーナツが足りないよ...');
        return;
      }
      setPendingBuyId(`bgm:${track.id}`);
      return;
    }
    if (spendDonut(track.price)) {
      unlockBgm(track.id);
      onShowMessage('新しいBGM、一緒に聴こうね！', 4000);
      setTimeout(() => onPlayBgm(index), 100);
    }
    setPendingBuyId(null);
  };

  const handleAddTodo = () => {
    addTodo(todoInput);
    setTodoInput('');
  };

  const handleToggleTodo = (todo: TodoItem) => {
    toggleTodo(todo.id);
    if (!todo.done) {
      onShowMessage(
        TODO_PRAISES[Math.floor(Math.random() * TODO_PRAISES.length)],
        3000
      );
    }
  };

  const renderToggle = (label: string, value: boolean, onChange: () => void) => (
    <div className="mascot-panel__row">
      <span className="mascot-panel__label">{label}</span>
      <button
        className={`mascot-panel__toggle ${value ? 'mascot-panel__toggle--on' : ''}`}
        onClick={onChange}
      >
        <span className="mascot-panel__toggle-knob" />
      </button>
    </div>
  );

  return (
    <div className="mascot-panel">
      {/* ステータス（好感度Lv・ドーナツ） */}
      <div className="mascot-panel__status">
        <div className="mascot-panel__status-love">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 21C12 21 4 15.5 4 9.8 4 6.6 6.4 4.5 9 4.5c1.5 0 2.5.7 3 1.6.5-.9 1.5-1.6 3-1.6 2.6 0 5 2.1 5 5.3C20 15.5 12 21 12 21Z" />
          </svg>
          <span className="mascot-panel__status-lv">Lv.{affinityLevel}</span>
          <div className="mascot-panel__status-bar">
            <div
              className="mascot-panel__status-bar-fill"
              style={{
                width: `${getAffinityProgress(affinityExp, affinityLevel) * 100}%`,
              }}
            />
          </div>
        </div>
        <div className="mascot-panel__status-donuts">
          <img src="/assets/img/donut.svg" alt="donut" />
          <span>{donuts}</span>
        </div>
      </div>

      {/* タブ */}
      <div className="mascot-panel__tabs">
        {(
          [
            ['timer', 'タイマー'],
            ['stats', '統計'],
            ['dress', '着せ替え'],
            ['bgm', 'BGM'],
            ['todo', 'メモ'],
          ] as [Tab, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            className={`mascot-panel__tab ${tab === key ? 'mascot-panel__tab--active' : ''}`}
            onClick={() => switchTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mascot-panel__body">
        {/* タイマー設定 */}
        {tab === 'timer' && (
          <>
            {/* 円形タイマー */}
            <div className="mascot-panel__ring-wrap">
              <svg width={RING_SIZE} height={RING_SIZE}>
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  stroke="rgba(255, 255, 255, 0.08)"
                  strokeWidth={RING_STROKE}
                  fill="none"
                />
                <circle
                  cx={RING_SIZE / 2}
                  cy={RING_SIZE / 2}
                  r={RING_RADIUS}
                  stroke={ringColor}
                  strokeWidth={RING_STROKE}
                  strokeLinecap="round"
                  fill="none"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={(1 - timerProgress) * RING_CIRCUMFERENCE}
                  className="mascot-panel__ring-progress"
                />
              </svg>
              <div className="mascot-panel__ring-center">
                <span className="mascot-panel__ring-phase">
                  {timerPhase === 'break' ? 'BREAK' : 'WORK'}
                </span>
                <span className="mascot-panel__ring-time">
                  {formatTimer(currentSession.timerRemaining)}
                </span>
                <div className="mascot-panel__ring-actions">
                  <button
                    className="mascot-panel__ring-btn"
                    onClick={currentSession.isRunning ? stopTimer : startTimer}
                    title={
                      currentSession.isRunning
                        ? '一時停止'
                        : timerPhase
                          ? '再開'
                          : '開始'
                    }
                  >
                    {currentSession.isRunning ? (
                      <svg viewBox="0 0 12 12" fill="currentColor">
                        <rect x="2" y="1.5" width="3" height="9" rx="1" />
                        <rect x="7" y="1.5" width="3" height="9" rx="1" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 12 12" fill="currentColor">
                        <path d="M3 1.8v8.4c0 .6.65.97 1.17.66l6.8-4.2a.78.78 0 0 0 0-1.32l-6.8-4.2A.78.78 0 0 0 3 1.8Z" />
                      </svg>
                    )}
                  </button>
                  {timerPhase && (
                    <button
                      className="mascot-panel__ring-btn mascot-panel__ring-btn--ghost"
                      onClick={onResetTimer}
                      title="停止"
                    >
                      <svg viewBox="0 0 12 12" fill="currentColor">
                        <rect x="2.5" y="2.5" width="7" height="7" rx="1.5" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="mascot-panel__section">作業時間</div>
            <div className="mascot-panel__chips">
              {WORK_PRESETS.map((min) => (
                <button
                  key={min}
                  className={`mascot-panel__chip ${pomodoroSettings.workDuration === min ? 'mascot-panel__chip--active' : ''}`}
                  onClick={() => handlePreset('work', min)}
                >
                  {min}分
                </button>
              ))}
            </div>

            <div className="mascot-panel__section">休憩時間</div>
            <div className="mascot-panel__chips">
              {BREAK_PRESETS.map((min) => (
                <button
                  key={min}
                  className={`mascot-panel__chip ${pomodoroSettings.breakDuration === min ? 'mascot-panel__chip--active' : ''}`}
                  onClick={() => handlePreset('break', min)}
                >
                  {min}分
                </button>
              ))}
            </div>

            <div className="mascot-panel__section">今日の目標</div>
            <div className="mascot-panel__row">
              <input
                type="range"
                min="25"
                max="200"
                step="25"
                value={restSettings.dailyGoalMinutes}
                onChange={(e) =>
                  updateRestSettings({ dailyGoalMinutes: Number(e.target.value) })
                }
                className="mascot-panel__slider"
              />
              <span className="mascot-panel__value">
                {restSettings.dailyGoalMinutes}分
              </span>
            </div>

            <div className="mascot-panel__section">心配ライン</div>
            <div className="mascot-panel__row">
              <input
                type="range"
                min="60"
                max="300"
                step="30"
                value={restSettings.maxDailyMinutes}
                onChange={(e) =>
                  updateRestSettings({ maxDailyMinutes: Number(e.target.value) })
                }
                className="mascot-panel__slider mascot-panel__slider--warn"
              />
              <span className="mascot-panel__value">
                {restSettings.maxDailyMinutes}分
              </span>
            </div>
            <p className="mascot-panel__hint">
              超えたら空ちゃんが休憩を促すよ
            </p>

            <div className="mascot-panel__divider" />
            {renderToggle('休憩を自動開始', pomodoroSettings.autoStartBreaks, () =>
              updatePomodoroSettings({
                autoStartBreaks: !pomodoroSettings.autoStartBreaks,
              })
            )}
            {renderToggle('作業を自動開始', pomodoroSettings.autoStartPomodoros, () =>
              updatePomodoroSettings({
                autoStartPomodoros: !pomodoroSettings.autoStartPomodoros,
              })
            )}
            {renderToggle('サウンド', pomodoroSettings.soundEnabled, () =>
              updatePomodoroSettings({
                soundEnabled: !pomodoroSettings.soundEnabled,
              })
            )}
            {renderToggle('通知', pomodoroSettings.notificationEnabled, () =>
              updatePomodoroSettings({
                notificationEnabled: !pomodoroSettings.notificationEnabled,
              })
            )}
            {renderToggle('ひとりごと', mascotRandomTalk, () =>
              setMascotRandomTalk(!mascotRandomTalk)
            )}
            {renderToggle('休憩うながし', restSettings.reminderLevel !== 'off', () =>
              updateRestSettings({
                reminderLevel: restSettings.reminderLevel === 'off' ? 'gentle' : 'off',
              })
            )}

            {/* 開発環境のみ: 休息うながしの選択肢を強制表示するテストボタン */}
            {import.meta.env.DEV && onDebugRestChoice && (
              <>
                <div className="mascot-panel__divider" />
                <button
                  className="mascot-panel__debug-btn"
                  onClick={onDebugRestChoice}
                >
                  休憩うながしテスト（開発用）
                </button>
              </>
            )}
          </>
        )}

        {/* 統計 */}
        {tab === 'stats' && (
          <>
            <div className="mascot-panel__stat-grid">
              <div className="mascot-panel__stat">
                <span className="mascot-panel__stat-value">
                  {formatMinutes(stats.today)}
                </span>
                <span className="mascot-panel__stat-label">今日</span>
              </div>
              <div className="mascot-panel__stat">
                <span className="mascot-panel__stat-value">
                  {formatMinutes(stats.week)}
                </span>
                <span className="mascot-panel__stat-label">直近7日</span>
              </div>
              <div className="mascot-panel__stat">
                <span className="mascot-panel__stat-value">{stats.streak}日</span>
                <span className="mascot-panel__stat-label">連続</span>
              </div>
              <div className="mascot-panel__stat">
                <span className="mascot-panel__stat-value">
                  {formatMinutes(stats.total)}
                </span>
                <span className="mascot-panel__stat-label">累計</span>
              </div>
            </div>

            <div className="mascot-panel__section">直近7日の作業時間</div>
            <div className="mascot-panel__chart">
              {stats.days.map((d, i) => (
                <div key={i} className="mascot-panel__bar-col">
                  <div
                    className="mascot-panel__bar"
                    style={{ height: `${(d.minutes / stats.maxDay) * 100}%` }}
                    title={`${d.minutes}分`}
                  />
                  <span className="mascot-panel__bar-label">{d.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 着せ替え */}
        {tab === 'dress' && (
          <>
            <div className="mascot-panel__donuts">
              <img src="/assets/img/donut.svg" alt="donut" />
              <span>{donuts}</span>
            </div>
            {dressList.map((d) => {
              const unlocked = dressUnlocks[d.id] || d.unlocked;
              const isSelected = d.id === selectedDress;
              const isPending = pendingBuyId === `dress:${d.id}`;
              return (
                <button
                  key={d.id}
                  className={`mascot-panel__item ${isSelected ? 'mascot-panel__item--selected' : ''} ${!unlocked ? 'mascot-panel__item--locked' : ''}`}
                  onClick={() => handleDressClick(d.id, d.price)}
                >
                  <span className="mascot-panel__item-name">
                    {dressNames[d.nameKey]?.[language] ?? d.id}
                  </span>
                  <span className="mascot-panel__item-sub">
                    {isSelected
                      ? '着用中'
                      : unlocked
                        ? ''
                        : isPending
                          ? `もう一度タップで購入（${d.price}）`
                          : `${d.price} donut`}
                  </span>
                </button>
              );
            })}
          </>
        )}

        {/* BGM */}
        {tab === 'bgm' && (
          <>
            <div className="mascot-panel__donuts">
              <img src="/assets/img/donut.svg" alt="donut" />
              <span>{donuts}</span>
            </div>
            {bgmList.map((track, index) => {
              const isPlayingThis = isBgmPlaying && currentBgmIndex === index;
              const isPending = pendingBuyId === `bgm:${track.id}`;
              return (
                <button
                  key={track.id}
                  className={`mascot-panel__item ${isPlayingThis ? 'mascot-panel__item--selected' : ''} ${!track.unlocked ? 'mascot-panel__item--locked' : ''}`}
                  onClick={() => handleBgmClick(track, index)}
                >
                  <span className="mascot-panel__item-name">{track.name}</span>
                  <span className="mascot-panel__item-sub">
                    {track.unlocked
                      ? isPlayingThis
                        ? '再生中（タップで停止）'
                        : ''
                      : isPending
                        ? `もう一度タップで購入（${track.price}）`
                        : `${track.price} donut`}
                  </span>
                </button>
              );
            })}
            {isBgmPlaying && (
              <button className="mascot-panel__stop-btn" onClick={onStopBgm}>
                BGMを停止
              </button>
            )}
          </>
        )}

        {/* ToDoメモ */}
        {tab === 'todo' && (
          <>
            <div className="mascot-panel__todo-form">
              <input
                className="mascot-panel__todo-input"
                value={todoInput}
                placeholder="やること・メモ"
                maxLength={60}
                onChange={(e) => setTodoInput(e.target.value)}
                onKeyDown={(e) => {
                  // 日本語入力の変換確定Enterでは追加しない
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    handleAddTodo();
                  }
                }}
              />
              <button className="mascot-panel__todo-add" onClick={handleAddTodo}>
                追加
              </button>
            </div>
            {todos.length === 0 ? (
              <div className="mascot-panel__todo-empty">
                メモはまだないよ。
                <br />
                書いても書かなくても、えらい！
              </div>
            ) : (
              todos.map((todo) => (
                <div
                  key={todo.id}
                  className={`mascot-panel__todo ${todo.done ? 'mascot-panel__todo--done' : ''}`}
                >
                  <button
                    className="mascot-panel__todo-check"
                    onClick={() => handleToggleTodo(todo)}
                    title={todo.done ? '未完了に戻す' : '完了にする'}
                  >
                    {todo.done && (
                      <svg
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2.5 6.5 5 9l4.5-6" />
                      </svg>
                    )}
                  </button>
                  <span className="mascot-panel__todo-text">{todo.text}</span>
                  <button
                    className="mascot-panel__todo-del"
                    onClick={() => removeTodo(todo.id)}
                    title="削除"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MascotPanel;
